import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { challengeSelectors, challengeActions } from 'redux/challenge';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'helpers';
import AutoCompleteField from 'components/FinalForm/Fields/AutoCompleteField';
import useFeature from 'hooks/useFeature';
import { fieldValidator, isRequired } from 'validators';
import PostFormContext from '../../PostFormContext';

const categoryRequired = fieldValidator('activity.categoryId', isRequired);

const ActivityCategorySelectField = ({ required, ...args }) => {
  const { t } = useTranslation(['common', 'component']);

  const dispatch = useDispatch();

  const challengeFeature = useFeature('challenges');

  const categories = useSelector(challengeSelectors.getActivityCategories);
  const { challengeId: formChallengeId } = useContext(PostFormContext);
  const challengeId = (/^#?\/challenges\/(\d+)\/?$/.exec(isMobile ? window.location.hash : window.location.pathname)
    || [])[1] ?? formChallengeId;
  const challengeCategories = useSelector(state => challengeSelectors.getCategoriesForChallenge(state, challengeId));

  useEffect(() => {
    dispatch(challengeActions.getActivityCategories());
  }, [dispatch]);

  const isChallengeCategory = (category) => challengeCategories.map(c => c.id).includes(category.id);

  const formatCategoryForAutoComplete = (category) => {
    // Defaults, if no active challenge
    let group = 'otherCategories';
    let score = 3;

    if (category.hasActiveChallenge) {
      group = 'activeChallengeCategories';
      score = 2;
    }

    if (isChallengeCategory(category)) {
      group = 'currentCategories';
      score = 1;
    }

    return {
      id: category.id,
      label: category.name,
      group: t(`component:activityForm.${group}`),
      score,
      hidden: !category.hasActiveChallenge && challengeFeature.hideUnusedActivityCategories,
    };
  };

  const options = categories
    .map(formatCategoryForAutoComplete)
    .filter(category => !category.hidden)
    .sort((a, b) => {
      if (a.score !== b.score) {
        return a.score - b.score;
      }

      return a.label.localeCompare(b.label);
    });

  if (options.length === 0 && !required) {
    return null;
  }

  const totalOptionGroups = [...new Set(options.map(option => option.group))].length;

  return (
    <AutoCompleteField
      {...args}
      validate={required ? categoryRequired : undefined}
      options={options}
      getOptionLabel={option => option?.label || ''}
      parse={option => option?.id || ''}
      format={id => options.find(option => option.id === id)}
      groupBy={option => totalOptionGroups > 1 ? option.group : undefined}
      multiple={false}
      label={t('component:activityForm.categoryLabel')}
      placeholder={t('component:activityForm.categoryPlaceholder')}
    />
  );
};

ActivityCategorySelectField.propTypes = {
  required: PropTypes.bool,
};

export default ActivityCategorySelectField;
